import React, { useState, useEffect } from 'react';
import {
  Send,
  Copy,
  Check,
  Loader,
  ArrowLeft,
  Upload,
  Sparkles,
  Mail,
  Save,
  Edit,
  AlertCircle,
  Trash2,
  ChevronDown,
  Clock,
  CheckCircle,
  XCircle,
  Archive,
  X,
  MessageCircle, 
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  createEmail,
  getProfessorInteractions,
  getProfile,
  getEmails,
  updateProfessorInteraction,
} from '../lib/api';
import { generateEmail } from '../lib/xai';
import type { ProfessorInteraction, Profile, Email } from '../lib/types';
import { supabase } from '../lib/supabase';
import { SUBSCRIPTION_TIERS, checkUsageLimit } from '../lib/stripe';
import { SubscriptionGate } from './SubscriptionGate';
import { EmailSidebar } from './EmailSidebar';
import { EmailPreview } from './EmailPreview';
import { ChatInterface } from './ChatInterface';
import { trackEmailOpen, trackEmailClick } from '../lib/analytics';
import { motion, AnimatePresence } from 'framer-motion';
import { sendMailgunEmail } from '../lib/mailgun';
import { ProfessorReplies } from './ProfessorReplies';

interface EmailTemplate {
  id: string;
  name: string;
  description: string;
  subject: string;
  body: string;
  subject_template: string;
  body_template: string;
  variables: string[];
}

export function EmailGenerator() {
  const navigate = useNavigate();
  const location = useLocation();
  const [professorName, setProfessorName] = useState('');
  const [university, setUniversity] = useState('');
  const [department, setDepartment] = useState('');
  const [researchInterest, setResearchInterest] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generatedEmail, setGeneratedEmail] = useState<EmailTemplate | null>(null);
  const [interactions, setInteractions] = useState<ProfessorInteraction[]>([]);
  const [selectedInteraction, setSelectedInteraction] = useState<ProfessorInteraction | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [resume, setResume] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [success, setSuccess] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [subscriptionTier, setSubscriptionTier] = useState<string>(SUBSCRIPTION_TIERS.FREE);
  const [savedDrafts, setSavedDrafts] = useState<Email[]>([]);
  const [selectedDraft, setSelectedDraft] = useState<Email | null>(null);
  const [showDrafts, setShowDrafts] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [showProfessorList, setShowProfessorList] = useState(false);

  useEffect(() => {
    let mounted = true;
    let loadingTimeout: NodeJS.Timeout | null = null;

    const loadData = async () => {
      try {
        loadingTimeout = setTimeout(() => {
          if (mounted && interactions.length === 0) {
            setInteractions([]); 
          }
        }, 1500);

        const [interactionsData, draftsData, templatesData] = await Promise.all([
          getProfessorInteractions(),
          supabase
            .from('emails')
            .select('*, professor_interactions(professor_name, university)')
            .eq('status', 'draft')
            .order('created_at', { ascending: false }),
          supabase
            .from('email_templates')
            .select('*')
            .order('created_at', { ascending: false })
        ]);

        if (mounted) {
          setInteractions(interactionsData);
          if (!draftsData.error && draftsData.data) {
            setSavedDrafts(draftsData.data);
          }
          if (!templatesData.error && templatesData.data) {
            setTemplates(templatesData.data);
          }

          const interactionId = location.state?.interactionId;
          if (interactionId) {
            const interaction = interactionsData.find(i => i.id === interactionId);
            if (interaction) {
              setSelectedInteraction(interaction);
              setProfessorName(
                interaction.professor_name.replace(
                  /^(Dr\.|Professor|Prof\.)\s+/i,
                  ''
                )
              );
              setUniversity(interaction.university || '');
              if (interaction.notes) {
                const interests = interaction.notes.replace(
                  'Research interests: ',
                  ''
                );
                setResearchInterest(interests.split(', ')[0]);
              }
            }
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Failed to load data. Please try again.');
      } finally {
        if (mounted && loadingTimeout) {
          clearTimeout(loadingTimeout);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
      if (loadingTimeout) clearTimeout(loadingTimeout);
    };
  }, [location.state?.interactionId]);

  const handleTemplateSelect = (template: EmailTemplate) => {
    if (!selectedInteraction) return;

    const variables: Record<string, string> = {
      professor_name: selectedInteraction.professor_name,
      university: selectedInteraction.university || '',
      research_area: researchInterest,
      student_name: profile?.full_name || '[Your name]',
      grade_level: profile?.grade_level || '[Your grade level]',
      school_name: profile?.school || '[Your school]',
    };

    const subject = template.subject_template.replace(
      /\{\{([^}]+)\}\}/g,
      (_, key) => variables[key.trim()] || ''
    );

    const body = template.body_template.replace(
      /\{\{([^}]+)\}\}/g,
      (_, key) => variables[key.trim()] || ''
    );

    setGeneratedEmail({ ...template, subject, body });
  };

  // Allow user to edit after generation
  const handleEdit = (field: 'subject' | 'body', value: string) => {
    setGeneratedEmail(prev => prev ? ({ ...prev, [field]: value }) : null);
  };

  const handleGenerateEmail = async () => {
    if (!selectedInteraction) {
      setError('Please select a professor');
      return;
    }

    // Check email generation limit
    const { allowed, message } = await checkUsageLimit('email');
    if (!allowed) {
      setError(message);
      return;
    }

    setLoading(true);
    setIsGenerating(true);
    setError(null);
    setGeneratedEmail(null);

    try {
      const response = await generateEmail({
        professorName: selectedInteraction.professor_name.replace(
          /^(Dr\.|Professor|Prof\.)\s+/i,
          ''
        ),
        professorResearchInterests: [researchInterest],
        professorDepartment: department,
        professorUniversity: selectedInteraction.university || '',
        studentProfile: {},
      });

      if (response.error) {
        throw new Error(response.error);
      }

      setGeneratedEmail(response as any);
    } catch (error) {
      console.error('Error generating email:', error);
      setError('Failed to generate email. Please try again.');
    } finally {
      setLoading(false);
      setIsGenerating(false);
    }
  };

  function SendEmailButton({ subject, body, email }: { subject: string; body: string; email: string }) {
    const [sending, setSending] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSendEmail = async () => {
      setSending(true);
      setError(null);
      
      try {
        if (import.meta.env.DEV) {
          await new Promise(resolve => setTimeout(resolve, 1500));
          console.log('DEV MODE: Simulating email send to:', email);
          console.log('Subject:', subject);
          console.log('Body:', body);
          
          setSuccess(true);
          setTimeout(() => setSuccess(false), 3000);
          
          if (selectedInteraction) {
            await updateProfessorInteraction(selectedInteraction.id, {
              status: 'contacted'
            });
          }
          return;
        }
        
        // Production mode - actually send the email
        const result = await sendMailgunEmail(
          email, 
          subject, 
          body,
          undefined, // html
          selectedInteraction?.id // Pass interaction ID to tie replies to this interaction
        );
        
        if (!result.success) {
          throw new Error(result.message);
        }
        
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
        
        if (selectedInteraction) {
          await updateProfessorInteraction(selectedInteraction.id, {
            status: 'contacted'
          });
        }
      } catch (err) {
        console.error('Error sending email:', err);
        setError(err instanceof Error ? err.message : 'Failed to send email. Please try again.');
      } finally {
        setSending(false);
      }
    };

    if (success) {
      return (
        <div className="flex items-center space-x-2 text-emerald-400">
          <Check className="h-5 w-5" />
          Email sent successfully!
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {error && (
          <div className="rounded-md bg-red-900/30 border border-red-800/30 p-2 text-sm">
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <span className="text-red-300">{error}</span>
            </div>
          </div>
        )}
        
        <motion.button
          onClick={handleSendEmail}
          disabled={sending}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors duration-200"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {sending ? (
            <>
              <Loader className="animate-spin mr-2 h-4 w-4 text-white" />
              Sending...
            </>
          ) : (
            <>
              <Mail className="mr-2 h-4 w-4" />
              Send Email
            </>
          )}
        </motion.button>
      </div>
    );
  }

  return (
    <motion.div 
      className="h-screen flex flex-col bg-gray-900"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.4 }}
    >
      {/* Professor Selection Header */}
      <div className="border-b border-gray-700/50 bg-gray-800">
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <h2 className="text-xl font-semibold text-white">Email Generator</h2>
            
            {/* Professor Dropdown with Preview - Full width on mobile */}
            <div className="relative w-full sm:min-w-[280px] sm:max-w-md">
              <div 
                onClick={() => setShowProfessorList(!showProfessorList)}
                className="flex items-center justify-between bg-gray-900 rounded-lg px-4 py-3 cursor-pointer border border-gray-700 hover:border-gray-600 transition-colors"
              >
                {selectedInteraction ? (
                  <div className="flex items-center space-x-3 overflow-hidden">
                    <span className={`flex-shrink-0 p-1.5 rounded-full ${getStatusColor(selectedInteraction.status)}`}>
                      {getStatusIcon(selectedInteraction.status)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-white truncate">
                        {selectedInteraction.professor_name.replace(/^(Dr\.|Professor|Prof\.)\s+/i, '')}
                      </p>
                      <p className="text-xs text-gray-400 truncate">
                        {selectedInteraction.university}
                      </p>
                    </div>
                  </div>
                ) : (
                  <span className="text-gray-400">Select a professor</span>
                )}
                <ChevronDown className={`flex-shrink-0 h-5 w-5 text-gray-400 transition-transform ${showProfessorList ? 'rotate-180' : ''}`} />
              </div>
              
              {/* Dropdown List - Fixed position on mobile, absolute on desktop */}
              <AnimatePresence>
                {showProfessorList && (
                  <motion.div 
                    className="fixed inset-x-0 top-auto bottom-0 sm:absolute sm:inset-auto sm:mt-2 sm:w-full z-50 bg-gray-800 border border-gray-700 sm:rounded-lg shadow-xl overflow-hidden"
                    initial={{ opacity: 0, y: 50, height: 0 }}
                    animate={{ opacity: 1, y: 0, height: 'auto' }}
                    exit={{ opacity: 0, y: 50, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <div className="flex justify-between items-center p-3 border-b border-gray-700 sm:hidden">
                      <h3 className="text-white font-medium">Select Professor</h3>
                      <button 
                        className="text-gray-400 hover:text-white"
                        onClick={() => setShowProfessorList(false)}
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="max-h-[50vh] sm:max-h-[320px] overflow-y-auto p-1">
                      {interactions.map((interaction, index) => (
                        <motion.button
                          key={interaction.id}
                          onClick={() => {
                            setSelectedInteraction(interaction);
                            setShowProfessorList(false);
                          }}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-700 focus:outline-none transition-colors ${
                            selectedInteraction?.id === interaction.id ? 'bg-gray-700' : ''
                          }`}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: index * 0.03, duration: 0.2 }}
                        >
                          <div className="flex items-center space-x-3">
                            <span className={`p-1.5 rounded-full ${getStatusColor(interaction.status)}`}>
                              {getStatusIcon(interaction.status)}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium text-white truncate">
                                {interaction.professor_name.replace(/^(Dr\.|Professor|Prof\.)\s+/i, '')}
                              </p>
                              <p className="text-xs text-gray-400 truncate">
                                {interaction.university}
                              </p>
                            </div>
                          </div>
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>

      {/* Main Email Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="container mx-auto px-6 py-6 max-w-4xl">
          {error && (
            <motion.div
              className="rounded-xl bg-red-900/20 border border-red-800/30 p-4 mb-6"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex items-center space-x-3">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <span className="text-sm text-red-300">{error}</span>
              </div>
            </motion.div>
          )}

          {selectedInteraction ? (
            <motion.div
              className="bg-gray-800 shadow-lg rounded-xl overflow-hidden border border-gray-700"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.3 }}
            >
              <div className="p-6 border-b border-gray-700/50">
                <div className="flex flex-col space-y-4">
                  <div className="mb-4 md:mb-0">
                    <h2 className="text-xl font-bold text-white">
                      Email to {selectedInteraction.professor_name}
                    </h2>
                    <h3 className="text-gray-400">
                      {selectedInteraction.email}
                    </h3>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                    <select
                      value={selectedTemplate?.id || ''}
                      onChange={(e) => {
                        const template = templates.find(
                          (t) => t.id === e.target.value
                        );
                        if (template) handleTemplateSelect(template);
                      }}
                      className="block w-full rounded-lg bg-gray-900 border border-gray-700 text-gray-300 focus:ring-indigo-500 focus:border-indigo-500 py-2 px-3"
                    >
                      <option value="">Select a template</option>
                      {templates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </select>
                    <button
                      onClick={() => setShowTemplateEditor(true)}
                      className="text-sm text-indigo-400 hover:text-indigo-300 bg-gray-700/30 px-4 py-2 rounded-lg flex-shrink-0 text-center"
                    >
                      Create Template
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <motion.button
                  onClick={handleGenerateEmail}
                  disabled={isGenerating}
                  className="w-full flex justify-center items-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-colors duration-200"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {isGenerating ? (
                    <>
                      <Loader className="animate-spin -ml-1 mr-2 h-5 w-5" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="-ml-1 mr-2 h-5 w-5" />
                      Generate Email
                    </>
                  )}
                </motion.button>
              </div>

              {generatedEmail && generatedEmail.subject && generatedEmail.body && (
                <div className="border-t border-gray-700">
                  <div className="bg-gray-800 rounded-b-xl">
                    <div className="p-4 flex justify-between items-center border-b border-gray-700/50">
                      <div>
                        <h3 className="text-base font-medium text-white">Email Preview</h3>
                        <p className="text-sm text-gray-400">{selectedInteraction.email}</p>
                      </div>
                      <motion.button
                        onClick={() => {
                          navigator.clipboard.writeText(`Subject: ${generatedEmail.subject}\n\n${generatedEmail.body}`);
                          setCopied(true);
                          setTimeout(() => setCopied(false), 2000);
                        }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white transition-colors duration-200"
                      >
                        {copied ? (
                          <>
                            <Check className="h-4 w-4 mr-1.5 text-green-400" />
                            Copied
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-1.5" />
                            Copy
                          </>
                        )}
                      </motion.button>
                    </div>

                    <div className="p-4 space-y-4">
                      <div>
                        <h4 className="text-sm font-medium text-gray-300">Subject:</h4>
                        <input 
                          type="text"
                          className="mt-1 text-sm w-full bg-gray-900 border border-gray-700 rounded-lg text-white p-2 focus:ring-indigo-500 focus:border-indigo-500"
                          value={generatedEmail.subject}
                          onChange={(e) => handleEdit('subject', e.target.value)}
                        />
                      </div>

                      <div>
                        <h4 className="text-sm font-medium text-gray-300">Body:</h4>
                        <textarea 
                          className="mt-1 whitespace-pre-wrap text-sm font-mono w-full bg-gray-900 border border-gray-700 rounded-lg text-white p-2 focus:ring-indigo-500 focus:border-indigo-500" 
                          rows={12}
                          value={generatedEmail.body}
                          onChange={(e) => handleEdit('body', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="px-4 py-3 bg-gray-800 border-t border-gray-700/30">
                      <SendEmailButton 
                        subject={generatedEmail.subject} 
                        body={generatedEmail.body} 
                        email={selectedInteraction.email ?? ""} 
                      />
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              className="flex flex-col items-center justify-center min-h-[400px] text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.3 }}
            >
              <Mail className="h-16 w-16 text-gray-600 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No Professor Selected</h3>
              <p className="text-gray-400 max-w-md">
                Select a professor above to generate an email.
              </p>
            </motion.div>
          )}
        </div>
      </div>

      {/* Template Editor Modal */}
      <AnimatePresence>
        {showTemplateEditor && (
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              {/* Update EmailTemplateEditor to work with dark theme */}
              <div className="p-6 border-b border-gray-700">
                <h2 className="text-xl font-bold text-white">Create Email Template</h2>
                <p className="mt-1 text-sm text-gray-400">
                  Design a reusable template for your professor emails
                </p>
              </div>

              {/* Template form would go here - would need to update EmailTemplateEditor component */}
              <div className="p-6">
                <div className="flex justify-end space-x-4">
                  <button
                    onClick={() => setShowTemplateEditor(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-400 hover:text-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Save Template
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );

  // Helper functions for status icons and colors
  function getStatusIcon(status: string) {
    switch (status) {
      case 'new':
        return <Mail className="h-4 w-4" />;
      case 'contacted':
        return <Clock className="h-4 w-4" />;
      case 'responded':
        return <CheckCircle className="h-4 w-4" />;
      case 'no_response':
        return <XCircle className="h-4 w-4" />;
      case 'archived':
        return <Archive className="h-4 w-4" />;
      default:
        return <Mail className="h-4 w-4" />;
    }
  }

  function getStatusColor(status: string) {
    switch (status) {
      case 'new':
        return 'text-blue-400 bg-blue-900/30';
      case 'contacted':
        return 'text-yellow-400 bg-yellow-900/30';
      case 'responded':
        return 'text-emerald-400 bg-emerald-900/30';
      case 'no_response':
        return 'text-red-400 bg-red-900/30';
      case 'archived':
        return 'text-gray-400 bg-gray-800/50';
      default:
        return 'text-gray-400 bg-gray-800/50';
    }
  }
}
